import { IonIcon, IonSpinner } from "@ionic/react";
import { ticket } from "ionicons/icons";
import React from "react";
import { bikeStationPopupRequestSlice, ephemeralStore, ticketViewerSlice, useEphemeralSliceSetter } from "../../../store/ephemeral";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { getPurchasedSegmentData, isPurchasedSegmentFullyRefunded } from "../../../tools/purchasedItineraries.tools";
import {
    resolveItineraryDestinationLabel,
    resolveItineraryOriginLabel,
} from "../../../tools/savedItineraryDisplay.tools";
import { buildSbbChEnStationsSearchUrl, itineraryLegEndpointDisplayLabel } from "../../../tools/SbbChStationsSearchUrl.tools";
import { type ItineraryDTO, type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { RentalVehicleType } from "../../../types/dtos/api/rental/rental-leg-properties.dto";
import { SegmentationProviderId } from "../../../types/itinerary/segmentation-provider.enum";
import { resolveBikeStationDisplayName } from "../../MapLibre/bikeStations/bikeStationPopupModern.tools";
import PriceConversion from "../pricing/PriceConversion";
import ItineraryRealtimeClock, { itineraryDestinationHasConfirmedRealtime } from "../shared/ItineraryRealtimeClock";
import ItineraryTransportIcon from "../shared/ItineraryTransportIcon";
import { formatItineraryTime, toggleSelectedItineraryLeg } from "./itineraryDetails.tools";
import ItineraryDetailsLeg from "./ItineraryDetailsLeg";
import {
    getSegmentOfferLinkLabel,
    normalizeSegmentOfferDisplayPrice,
    SEGMENT_BIKE_STATION_LINK_LABEL,
    SEGMENT_TICKET_NOT_PURCHASED_LABEL,
    SEGMENT_TICKET_PURCHASED_LABEL,
    SEGMENT_TICKET_REFUNDED_LABEL,
} from "./offerPricing/helpers/OfferPricingSegmentDisplay.helper";
import { computeSegmentOfferDisplayData } from "./offerPricing/OfferPricingFacade";

interface AddressLike {
    "result"?:  string | null;
    "address"?: string | null;
}

/**
 * Props for the itinerary details side panel (header, summary, and optional leg timeline).
 */
interface ItineraryDetailsPanelProps {
    "selectedItinerary":         ItineraryDTO;
    "departureSelectedAddress"?: AddressLike | null;
    "selectedAddress"?:          AddressLike | null;
    "legDetails":                boolean;
    "selectedLeg":               ItineraryLegDTO | null;
    "setSelectedLeg":            ( leg: ItineraryLegDTO | null ) => void;
    "focusMapOnPoint":           ( point: {
        "lat": number;
        "lon": number;
    } ) => void;
    "isEuro":             boolean;
    "isDarkUi":           boolean;
    "offerStatus"?:       "idle" | "loading" | "success" | "error";
    "pricingPreferences": ItineraryPricingPreferencesDTO;

    /** When `false`, hides segment and total pricing UI (saved-itinerary details). */
    "showPrice"?: boolean;

    /** Marks ticket-purchased segment strips (`offer.segments[*].purchased` markers). */
    "isPurchasedItinerary"?: boolean;
}

const DEFAULT_DEPARTURE_LABEL = "Votre Position";
const MY_POSITION_ICON_MODE = "myposition";
const DESTINATION_ICON_MODE = "destination";
const SEGMENT_COLUMN_PLACEHOLDER_CLASS_NAME = "w-[35px] shrink-0 rounded-lg border border-transparent bg-transparent";

/**
 * Parses an ISO datetime string into epoch milliseconds for timeline display.
 *
 * @param value - ISO datetime string from a leg step, when present.
 * @returns Epoch milliseconds, or `null` when the value is absent or invalid.
 */
const toTimestamp = ( value: string | undefined ): number | null => {
    if ( value == null ) {
        return null;
    }

    const parsed = new Date( value ).getTime();

    return Number.isNaN( parsed ) ? null : parsed;
};

/**
 * Narrows a leg step `time` field to string for {@link toTimestamp}.
 *
 * @remarks Uses runtime narrowing to avoid passing checker `error`-typed values when TypeScript
 * resolves duplicate itinerary module instances.
 *
 * @param step - Leg endpoint step (`from` / `to`) with an optional `time` field.
 * @returns ISO datetime string when present and typed as string; otherwise `undefined`.
 */
const legStepTimeToIsoString = ( step: { "time"?: unknown } | undefined ): string | undefined => {
    if ( step === undefined ) {
        return undefined;
    }

    const raw: unknown = step["time"];

    return typeof raw === "string" ? raw : undefined;
};

/**
 * Narrows a leg endpoint `location` to map coordinates for {@link focusMapOnPoint}.
 *
 * @remarks Uses `Reflect.get` to avoid unsafe member access when TypeScript resolves `from`/`to`
 * as checker `error`.
 *
 * @param step - Leg endpoint step (`from` / `to`).
 * @returns Latitude/longitude pair, or `null` when coordinates are absent or invalid.
 */
const legStepLocationToMapPoint = ( step: unknown ): {
    "lat": number;
    "lon": number;
} | null => {
    if ( step == null || typeof step !== "object" ) {
        return null;
    }

    const loc: unknown = Reflect.get( step, "location" );

    if ( loc == null || typeof loc !== "object" ) {
        return null;
    }

    const lat: unknown = Reflect.get( loc, "lat" );
    const lon: unknown = Reflect.get( loc, "lon" );

    if ( typeof lat !== "number" || typeof lon !== "number" ) {
        return null;
    }

    return {
        lat,
        lon,
    };
};

interface SegmentRangeVisualState {
    "segmentId":     string;
    "firstLegIndex": number;
    "lastLegIndex":  number;
}

/**
 * Builds validated visual segment ranges from itinerary pricing segment leg ranges.
 *
 * @remarks The detail view uses first/last leg indexes to render one continuous segment block.
 *
 * @param itinerary - Loaded itinerary with optional `pricingSegments` leg ranges.
 * @returns Sorted segment ranges with valid leg indexes, omitting malformed backend rows.
 */
const buildSegmentRangeVisualStates = ( itinerary: ItineraryDTO ): SegmentRangeVisualState[] => {
    const maxLegIndex = itinerary.legs.length - 1;

    return ( itinerary.pricingSegments ?? [] ).
        map( ( segment ) => {
            const firstLegIndex = segment.legRange?.firstLegIndex;
            const lastLegIndex = segment.legRange?.lastLegIndex;

            if ( firstLegIndex == null || lastLegIndex == null ) {
                return null;
            }

            if ( firstLegIndex < 0 || lastLegIndex < firstLegIndex || lastLegIndex > maxLegIndex ) {
                return null;
            }

            return {
                "segmentId": segment.id,
                firstLegIndex,
                lastLegIndex,
            };
        } ).
        filter( ( segmentRange ): segmentRange is SegmentRangeVisualState => segmentRange !== null ).
        sort( ( a, b ) => a.firstLegIndex - b.firstLegIndex );
};


interface ItineraryDetailsEndpointRowProps {
    "domId":             string;
    "iconMode":          string;
    "label":             string;
    "timeMs":            number;
    "scheduledTimeIso"?: string;
    "hasRealtimeInfo"?:  boolean;
    "onClick"?:          () => void;
    "isInteractive":     boolean;
    "isDarkUi":          boolean;
}

const ItineraryDetailsEndpointRow: React.FC<ItineraryDetailsEndpointRowProps> = ( {
    domId,
    iconMode,
    label,
    timeMs,
    scheduledTimeIso,
    hasRealtimeInfo = false,
    onClick,
    isInteractive,
    isDarkUi,
} ) => {
    const palette = getUIColorPalette( isDarkUi );

    return (
        <div
            className={`flex items-center rounded-lg p-2 pr-1 transition-colors duration-200 ease-out ${ isInteractive ? "cursor-pointer hover:bg-ionic-primary/10 active:bg-ionic-primary/20" : "" }`}
            id={domId}
            onClick={onClick}
            role={isInteractive ? "button" : undefined}
            tabIndex={isInteractive ? 0 : undefined}
        >
            <div className="flex items-center justify-center pl-[34px] pr-2">
                {ItineraryTransportIcon( iconMode )}
            </div>
            <div className="flex w-full pr-0">
                <span
                    className="max-w-[95%] text-xs font-semibold"
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    {label}
                </span>
                {hasRealtimeInfo
                    ? (
                        <ItineraryRealtimeClock
                            alignEnd={true}
                            className="text-xs font-semibold"
                            hasRealtimeInfo={hasRealtimeInfo}
                            isDarkUi={isDarkUi}
                            neutralColor={palette.overlayTextSecondary}
                            scheduledTimeIso={scheduledTimeIso}
                            timeIso={new Date( timeMs ).toISOString()}
                        />
                    )
                    : (
                        <span
                            className="ml-auto text-right text-xs font-semibold"
                            style={{ "color": palette.overlayTextSecondary }}
                        >
                            {formatItineraryTime( timeMs )}
                        </span>
                    )}
            </div>
        </div>
    );
};

/**
 * Renders the selected itinerary details panel: timing header, condensed leg summary,
 * and optional expanded timeline with per-segment offer columns.
 */
const ItineraryDetailsPanel: React.FC<ItineraryDetailsPanelProps> = ( {
    selectedItinerary,
    departureSelectedAddress,
    selectedAddress,
    legDetails,
    selectedLeg,
    setSelectedLeg,
    focusMapOnPoint,
    isEuro,
    isDarkUi,
    offerStatus = "idle",
    pricingPreferences,
    showPrice = true,
    isPurchasedItinerary = false,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const isOfferLoading = showPrice && offerStatus === "loading";
    const firstLeg = selectedItinerary.legs[0];
    const lastLeg = selectedItinerary.legs[selectedItinerary.legs.length - 1];
    const departureLabel = departureSelectedAddress != null
        ? departureSelectedAddress.result ?? departureSelectedAddress.address ?? DEFAULT_DEPARTURE_LABEL
        : resolveItineraryOriginLabel( selectedItinerary ) || DEFAULT_DEPARTURE_LABEL;
    const destinationLabel = selectedAddress != null
        ? selectedAddress.result ?? selectedAddress.address ?? ""
        : resolveItineraryDestinationLabel( selectedItinerary );
    const departureTimeMs = toTimestamp( legStepTimeToIsoString( firstLeg?.from ) );
    const destinationTimeMs = toTimestamp( legStepTimeToIsoString( lastLeg?.to ) );
    const canFocusDeparture = legStepLocationToMapPoint( firstLeg?.from ) != null;
    const canFocusDestination = legStepLocationToMapPoint( lastLeg?.to ) != null;
    const segmentRanges = buildSegmentRangeVisualStates( selectedItinerary );
    const hasPricingSegments = ( selectedItinerary.pricingSegments?.length ?? 0 ) > 0;
    const setBikeStationPopupRequest = useEphemeralSliceSetter( bikeStationPopupRequestSlice );

    const openBikeStationPopupForLeg = ( leg: ItineraryLegDTO ): void => {
        const bikeStation = leg.rental?.bikeStation;
        if ( bikeStation === undefined ) {
            return;
        }

        setBikeStationPopupRequest( {
            "stationId": bikeStation.stationId,
            "operator":  bikeStation.operator,
            "name":      resolveBikeStationDisplayName( bikeStation.name ),
            "lat":       bikeStation.lat,
            "lon":       bikeStation.lon,
            "visible":   true,
        } );
    };

    const handleLegClick = ( leg: ItineraryLegDTO ): void => {
        if ( leg.rental?.bikeStation !== undefined ) {
            openBikeStationPopupForLeg( leg );

            return;
        }

        toggleSelectedItineraryLeg( selectedLeg, leg, setSelectedLeg );
    };

    return (
        <div
            className="flex min-h-0 flex-1 flex-col"
            id="itinerary-details-panel"
        >
            <div
                className="no-bounce scroll-container overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                id="itinerary-details-scroll"
                style={{
                    "flex":      1,
                    "minHeight": 0,
                }}
            >
                {/* Detailed timeline is optional and controlled by the drag panel state. */}
                {legDetails
                    ? (
                        <div
                            className="border-t-ionic-light-gray mt-1 px-2"
                            id="itinerary-details-timeline"
                        >
                            {firstLeg != null && departureTimeMs != null
                                ? (
                                    <>
                                        {/* Departure anchor row before the first leg. */}
                                        <div
                                            className="m-1 flex items-stretch"
                                            id="itinerary-details-departure-row"
                                        >
                                            <div
                                                aria-hidden={true}
                                                className={SEGMENT_COLUMN_PLACEHOLDER_CLASS_NAME}
                                            />
                                            <div className="min-w-0 flex-1 pr-2">
                                                <ItineraryDetailsEndpointRow
                                                    domId="itinerary-details-departure-endpoint"
                                                    iconMode={MY_POSITION_ICON_MODE}
                                                    isDarkUi={isDarkUi}
                                                    isInteractive={canFocusDeparture}
                                                    label={departureLabel}
                                                    onClick={canFocusDeparture
                                                        ? () => {
                                                            const point = legStepLocationToMapPoint( firstLeg?.from );
                                                            if ( point != null ) {
                                                                focusMapOnPoint( point );
                                                            }
                                                        }
                                                        : undefined}
                                                    timeMs={departureTimeMs}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )
                                : null}
                            {( () => {
                                const rows: React.ReactNode[] = [];
                                let legIndex = 0;
                                let segmentIndex = 0;

                                while ( legIndex < selectedItinerary.legs.length ) {
                                    const currentSegment = segmentRanges[segmentIndex];

                                    if ( currentSegment != null && currentSegment.firstLegIndex === legIndex ) {
                                        const segmentOfferDisplay = computeSegmentOfferDisplayData(
                                            selectedItinerary,
                                            currentSegment.segmentId,
                                            pricingPreferences,
                                        );
                                        const segmentSales = selectedItinerary.offer?.segments[currentSegment.segmentId];

                                        // Backend uses `null` when provider execution failed or was skipped (see OfferSegmentSalesDTO).
                                        const isProviderOffersNull =
                                            segmentSales != null && segmentSales.providerOffers === null;
                                        const purchasedSegment = getPurchasedSegmentData( selectedItinerary, currentSegment.segmentId );
                                        const isPurchasableUnpurchasedSegment =
                                            isPurchasedItinerary &&
                                            purchasedSegment === undefined &&
                                            segmentSales != null &&
                                            segmentSales.providerOffers !== null;
                                        const pricingSegmentDef = selectedItinerary.pricingSegments?.
                                            find( ( segment ) => segment.id === currentSegment.segmentId );
                                        const isSwissCffPricingSegment =
                                            pricingSegmentDef?.provider?.id === SegmentationProviderId.swiss_cff;
                                        const isRentalPricingSegment =
                                            pricingSegmentDef?.provider?.id === SegmentationProviderId.rental_motis;
                                        const segmentFirstLeg = selectedItinerary.legs[currentSegment.firstLegIndex];
                                        const isBikeStationSegment =
                                            isRentalPricingSegment &&
                                            segmentFirstLeg?.rental?.vehicle?.type === RentalVehicleType.BICYCLE &&
                                            segmentFirstLeg?.rental?.bikeStation != null;
                                        const segmentLastLeg = selectedItinerary.legs[currentSegment.lastLegIndex];
                                        const hasBookingUrl = segmentOfferDisplay?.bookingUrl != null;
                                        const segmentOfferLinkLabel = isBikeStationSegment
                                            ? SEGMENT_BIKE_STATION_LINK_LABEL
                                            :  segmentOfferDisplay != null
                                                ? getSegmentOfferLinkLabel( segmentOfferDisplay )
                                                : null;
                                        const segmentDisplayPrice = showPrice && segmentOfferDisplay != null
                                            ? normalizeSegmentOfferDisplayPrice( segmentOfferDisplay.price )
                                            : null;
                                        const fromStopLabel =
                                            ( typeof pricingSegmentDef?.from?.name === "string"
                                                ? pricingSegmentDef.from.name.trim()
                                                : "" ) ||
                                                itineraryLegEndpointDisplayLabel( segmentFirstLeg?.from );
                                        const toStopLabel =
                                            ( typeof pricingSegmentDef?.to?.name === "string"
                                                ? pricingSegmentDef.to.name.trim()
                                                : "" ) ||
                                                itineraryLegEndpointDisplayLabel( segmentLastLeg?.to );
                                        const segmentDepartureIso =
                                            ( typeof pricingSegmentDef?.from?.time === "string"
                                                ? pricingSegmentDef.from.time
                                                : undefined ) ??
                                                legStepTimeToIsoString( segmentFirstLeg?.from ) ??
                                                "";
                                        const sbbStationsSearchUrl =
                                            isProviderOffersNull &&
                                            isSwissCffPricingSegment &&
                                            fromStopLabel !== "" &&
                                            toStopLabel !== "" &&
                                            segmentDepartureIso !== ""
                                                ? buildSbbChEnStationsSearchUrl( {
                                                    "fromStopLabel":    fromStopLabel,
                                                    "toStopLabel":      toStopLabel,
                                                    "departureTimeIso": segmentDepartureIso,
                                                    "reduction":        pricingPreferences.swissReductionCard ?? "none",
                                                } )
                                                : null;
                                        const segmentSideLinkOpensExternal =
                                            hasBookingUrl || sbbStationsSearchUrl != null;
                                        const hasSegmentSideAction =
                                            segmentSideLinkOpensExternal || isBikeStationSegment;
                                        const shouldShowSegmentProviderPill =
                                            !isRentalPricingSegment &&
                                            segmentOfferDisplay?.providerKey !== "unknown";
                                        const isSegmentPriceUnavailable =
                                            !isOfferLoading &&
                                            isProviderOffersNull &&
                                            segmentOfferDisplay === null;
                                        const isSegmentStripInert = isPurchasableUnpurchasedSegment;
                                        const segmentGroupRowStyle: React.CSSProperties | undefined = isPurchasableUnpurchasedSegment
                                            ? { "borderColor": "color-mix(in srgb, var(--ion-color-danger) 25%, transparent)" }
                                            : undefined;
                                        const segmentStripButtonStyle: React.CSSProperties | undefined = isPurchasableUnpurchasedSegment
                                            ? {
                                                "borderColor": "color-mix(in srgb, var(--ion-color-danger) 30%, transparent)",
                                                "background":  "color-mix(in srgb, var(--ion-color-danger) 8%, transparent)",
                                                "color":       "var(--ion-color-danger)",
                                            }
                                            : isOfferLoading
                                                ? { "color": palette.statusDelayText }
                                                : undefined;

                                        if ( isSegmentPriceUnavailable ) {
                                            for ( let currentLegIndex = currentSegment.firstLegIndex; currentLegIndex <= currentSegment.lastLegIndex; currentLegIndex += 1 ) {
                                                const leg = selectedItinerary.legs[currentLegIndex];
                                                const nextLeg = selectedItinerary.legs[currentLegIndex + 1];
                                                const hiddenSegmentLegRow = (
                                                    <div
                                                        className="m-1 flex items-stretch"
                                                        id={`itinerary-details-leg-row-${ leg.id }`}
                                                        key={`${ leg.id }-${ currentLegIndex }`}
                                                    >
                                                        <div
                                                            aria-hidden={true}
                                                            className={SEGMENT_COLUMN_PLACEHOLDER_CLASS_NAME}
                                                        />
                                                        <div className="min-w-0 flex-1 pr-2">
                                                            <ItineraryDetailsLeg
                                                                hasPricingSegments={hasPricingSegments}
                                                                isDarkUi={isDarkUi}
                                                                isLegSelected={selectedLeg !== null && selectedLeg.id === leg.id}
                                                                leg={leg}
                                                                nextLeg={nextLeg}
                                                                onLegClick={handleLegClick}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                                rows.push( hiddenSegmentLegRow );
                                            }

                                            legIndex = currentSegment.lastLegIndex + 1;
                                            segmentIndex += 1;

                                            continue;
                                        }

                                        const segmentLegRows: React.ReactNode[] = [];
                                        for ( let currentLegIndex = currentSegment.firstLegIndex; currentLegIndex <= currentSegment.lastLegIndex; currentLegIndex += 1 ) {
                                            const leg = selectedItinerary.legs[currentLegIndex];
                                            const nextLeg = selectedItinerary.legs[currentLegIndex + 1];
                                            const segmentLegRow = (
                                                <ItineraryDetailsLeg
                                                    hasPricingSegments={hasPricingSegments}
                                                    isDarkUi={isDarkUi}
                                                    isLegSelected={selectedLeg !== null && selectedLeg.id === leg.id}
                                                    key={`${ leg.id }-${ currentLegIndex }`}
                                                    leg={leg}
                                                    nextLeg={nextLeg}
                                                    onLegClick={handleLegClick}
                                                />
                                            );
                                            segmentLegRows.push( segmentLegRow );
                                        }

                                        const segmentGroupRow = (
                                            <div
                                                className="m-0 grid grid-cols-[auto_minmax(0,1fr)] items-stretch rounded-lg border border-green-price/25"
                                                id={`itinerary-details-segment-${ currentSegment.segmentId }`}
                                                key={`segment-${ currentSegment.segmentId }-${ currentSegment.firstLegIndex }`}
                                                style={segmentGroupRowStyle}
                                            >
                                                <button
                                                    aria-label={purchasedSegment !== undefined
                                                        ? "Voir le billet acheté pour ce segment"
                                                        : isPurchasableUnpurchasedSegment
                                                            ? "Billet non acheté pour ce segment"
                                                            : isBikeStationSegment
                                                                ? "Voir la station sur la carte"
                                                                : sbbStationsSearchUrl != null
                                                                    ? "Ouvrir la recherche de trajet sur sbb.ch"
                                                                    : hasBookingUrl
                                                                        ? "Ouvrir l'offre segment dans un nouvel onglet"
                                                                        : "Offre segment indisponible"}
                                                    className={`flex h-full w-fit shrink-0 items-center justify-center rounded-l-lg border border-green-price/25 bg-green-price/10 p-[0.35rem] text-green-price transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-green-price/40 ${ purchasedSegment !== undefined ? "cursor-pointer hover:border-green-price/40 hover:bg-green-price/15" : hasSegmentSideAction && !isSegmentStripInert ? "cursor-pointer hover:border-green-price/40 hover:bg-green-price/15" : isSegmentStripInert ? "cursor-default" : "cursor-default opacity-80" }`}
                                                    data-leg-click-exempt="true"
                                                    id={`itinerary-details-segment-${ currentSegment.segmentId }-offer`}
                                                    onClick={() => {
                                                        if ( purchasedSegment !== undefined ) {
                                                            ephemeralStore.setValue( ticketViewerSlice, {
                                                                "isOpen":           true,
                                                                "itineraryId":      selectedItinerary.id,
                                                                "initialSegmentId": currentSegment.segmentId,
                                                            } );

                                                            return;
                                                        }

                                                        if ( isSegmentStripInert ) {
                                                            return;
                                                        }

                                                        if ( isBikeStationSegment ) {
                                                            openBikeStationPopupForLeg( segmentFirstLeg );

                                                            return;
                                                        }

                                                        if ( segmentOfferDisplay?.bookingUrl != null ) {
                                                            window.open( segmentOfferDisplay.bookingUrl, "_blank", "noopener,noreferrer" );

                                                            return;
                                                        }

                                                        if ( sbbStationsSearchUrl != null ) {
                                                            window.open( sbbStationsSearchUrl, "_blank", "noopener,noreferrer" );
                                                        }
                                                    }}
                                                    style={segmentStripButtonStyle}
                                                    type="button"
                                                >
                                                    <span className="inline-block w-fit whitespace-nowrap [writing-mode:vertical-rl] rotate-180">
                                                        {isOfferLoading && !isBikeStationSegment
                                                            ? (
                                                                <span className="inline-flex items-center">
                                                                    <IonSpinner
                                                                        className="h-3 w-3 shrink-0"
                                                                        style={{ "color": palette.statusDelayText }}
                                                                    />
                                                                </span>
                                                            )
                                                            : purchasedSegment !== undefined
                                                                ? (
                                                                    <span className="inline-flex flex-nowrap items-center gap-1 whitespace-nowrap">
                                                                        <IonIcon
                                                                            aria-hidden={true}
                                                                            className="h-3.5 w-3.5"
                                                                            icon={ticket}
                                                                        />
                                                                        <span className="text-xs font-semibold">
                                                                            {isPurchasedSegmentFullyRefunded( purchasedSegment )
                                                                                ? SEGMENT_TICKET_REFUNDED_LABEL
                                                                                : SEGMENT_TICKET_PURCHASED_LABEL}
                                                                        </span>
                                                                    </span>
                                                                )
                                                                : isPurchasableUnpurchasedSegment
                                                                    ? (
                                                                        <span className="whitespace-nowrap text-xs font-semibold">
                                                                            {SEGMENT_TICKET_NOT_PURCHASED_LABEL}
                                                                        </span>
                                                                    )
                                                                    : (
                                                                <span className="inline-flex flex-nowrap items-center gap-2 whitespace-nowrap">
                                                                    {segmentDisplayPrice != null
                                                                        ? (
                                                                            <PriceConversion
                                                                                chooseEuro={isEuro}
                                                                                className="whitespace-nowrap text-sm"
                                                                                compact={false}
                                                                                price={segmentDisplayPrice}
                                                                                showFromPrefix={false}
                                                                            />
                                                                        )
                                                                        : segmentOfferLinkLabel != null
                                                                            ? (
                                                                                <span className="whitespace-nowrap text-xs font-semibold">
                                                                                    {segmentOfferLinkLabel}
                                                                                </span>
                                                                            )
                                                                            : null}
                                                                    {shouldShowSegmentProviderPill
                                                                        ? (
                                                                            <span
                                                                                className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-solid border-green-price/45 bg-green-price/12 px-[5px] py-[3px] text-center text-[9px] font-semibold uppercase leading-tight tracking-wider text-green-price"
                                                                            >
                                                                                {segmentOfferDisplay?.providerName}
                                                                            </span>
                                                                        )
                                                                        : null}
                                                                    {segmentSideLinkOpensExternal
                                                                        ? (
                                                                            <span
                                                                                aria-hidden={true}
                                                                                className="text-[9px] font-bold opacity-70"
                                                                            >
                                                                                ↗
                                                                            </span>
                                                                        )
                                                                        : null}
                                                                </span>
                                                            )}
                                                    </span>
                                                </button>
                                                <div
                                                    className="min-w-0 m-[2px] flex flex-col justify-center"
                                                    id={`itinerary-details-segment-${ currentSegment.segmentId }-legs`}
                                                >
                                                    <div>
                                                        {segmentLegRows}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                        rows.push( segmentGroupRow );

                                        legIndex = currentSegment.lastLegIndex + 1;
                                        segmentIndex += 1;

                                        continue;
                                    }

                                    const leg = selectedItinerary.legs[legIndex];
                                    const nextLeg = selectedItinerary.legs[legIndex + 1];
                                    const nonSegmentRow = (
                                        <div
                                            className="m-1 flex items-stretch"
                                            id={`itinerary-details-leg-row-${ leg.id }`}
                                            key={`${ leg.id }-${ legIndex }`}
                                        >
                                            <div
                                                aria-hidden={true}
                                                className={SEGMENT_COLUMN_PLACEHOLDER_CLASS_NAME}
                                            />
                                            <div className="min-w-0 flex-1 pr-0">
                                                <ItineraryDetailsLeg
                                                    hasPricingSegments={hasPricingSegments}
                                                    isDarkUi={isDarkUi}
                                                    isLegSelected={selectedLeg !== null && selectedLeg.id === leg.id}
                                                    leg={leg}
                                                    nextLeg={nextLeg}
                                                    onLegClick={handleLegClick}
                                                />
                                            </div>
                                        </div>
                                    );
                                    rows.push( nonSegmentRow );
                                    legIndex += 1;
                                }

                                return rows;
                            } )()}
                            {lastLeg != null && destinationTimeMs != null
                                ? (
                                    <>
                                        {/* Destination anchor row after the last leg. */}
                                        <div
                                            className="m-1 flex items-stretch"
                                            id="itinerary-details-destination-row"
                                        >
                                            <div
                                                aria-hidden={true}
                                                className={SEGMENT_COLUMN_PLACEHOLDER_CLASS_NAME}
                                            />
                                            <div className="min-w-0 flex-1 pr-2">
                                                <ItineraryDetailsEndpointRow
                                                    domId="itinerary-details-destination-endpoint"
                                                    hasRealtimeInfo={itineraryDestinationHasConfirmedRealtime( selectedItinerary )}
                                                    iconMode={DESTINATION_ICON_MODE}
                                                    isDarkUi={isDarkUi}
                                                    isInteractive={canFocusDestination}
                                                    label={destinationLabel}
                                                    onClick={canFocusDestination
                                                        ? () => {
                                                            const point = legStepLocationToMapPoint( lastLeg?.to );
                                                            if ( point != null ) {
                                                                focusMapOnPoint( point );
                                                            }
                                                        }
                                                        : undefined}
                                                    scheduledTimeIso={lastLeg?.to?.scheduledTime}
                                                    timeMs={destinationTimeMs}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )
                                : null}
                        </div>
                    )
                    : null}
            </div>
        </div>
    );
};

export default ItineraryDetailsPanel;
